import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router';
import {
  filterRecommendationHistory,
  HISTORY_FILTER_LABELS,
  HISTORY_FILTERS,
  REFERRAL_STATUS_LABELS,
  trusteeName,
  type HistoryFilter,
  type RecommendationHistoryRow,
  type StaffRecommendationHistoryRow,
  type TrusteeRecommendationSummary,
} from '@hv/shared';
import { useMyRecommendationHistory, useRecommendationHistory } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { describeError } from '../components/Notify';
import { useUrlState } from '../urlState';

const DAY: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
const fmtInstant = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, DAY) : '—');
const fmtDay = (ymd: string | null) => (ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, DAY) : '—');
const familyLabel = (r: RecommendationHistoryRow) => (r.familyLastName ? `${r.familyLastName} family` : 'Untitled recommendation');
const page = { p: 2 } as const;

function StateChips({ row }: { row: RecommendationHistoryRow }) {
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      <Chip size="small" label={REFERRAL_STATUS_LABELS[row.status]} color={row.status === 'draft' ? 'default' : 'primary'} />
      {row.expired ? <Chip size="small" variant="outlined" label="Expired" /> : row.active && <Chip size="small" color="success" label="Active" />}
    </Stack>
  );
}

function FamilyCell({ row, link }: { row: RecommendationHistoryRow; link: ReactNode }) {
  const details = [row.childNames.join(', '), row.referenceNumber].filter(Boolean);
  return (
    <TableCell>
      {link}
      {details.map((d) => (
        <Typography key={d} variant="caption" color="text.secondary" display="block">
          {d}
        </Typography>
      ))}
    </TableCell>
  );
}

function HistoryControls({ show, onShow, q, onQ, placeholder }: { show: HistoryFilter; onShow: (v: HistoryFilter) => void; q: string; onQ: (v: string) => void; placeholder: string }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }} mb={2}>
      <TextField
        size="small"
        placeholder={placeholder}
        value={q}
        onChange={(e) => onQ(e.target.value)}
        sx={{ width: { xs: '100%', sm: 340 } }}
        slotProps={{
          htmlInput: { 'aria-label': 'Search recommendations' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v: HistoryFilter | null) => v && onShow(v)} aria-label="Filter by state">
        {HISTORY_FILTERS.map((f) => (
          <ToggleButton key={f} value={f}>
            {HISTORY_FILTER_LABELS[f]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

function HistoryTable<T extends RecommendationHistoryRow>({
  rows,
  total,
  trusteeColumn,
  family,
}: {
  rows: T[];
  total: number;
  trusteeColumn?: (row: T) => ReactNode;
  family: (row: T) => ReactNode;
}) {
  const columns = trusteeColumn ? 7 : 6;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Recommendation history">
        <TableHead>
          <TableRow>
            <TableCell>Family</TableCell>
            {trusteeColumn && <TableCell>Trustee</TableCell>}
            <TableCell>Academic year</TableCell>
            <TableCell>Approved</TableCell>
            <TableCell>Used</TableCell>
            <TableCell>Expires</TableCell>
            <TableCell>State</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid="history-row" sx={{ verticalAlign: 'top' }}>
              <FamilyCell row={r} link={family(r)} />
              {trusteeColumn && <TableCell>{trusteeColumn(r)}</TableCell>}
              <TableCell>{r.cycle?.label ?? '—'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtInstant(r.approvedAt)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtInstant(r.usedAt)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(r.expiresOn)}</TableCell>
              <TableCell>
                <StateChips row={r} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns}>{total === 0 ? 'No recommendations on record yet.' : 'No recommendations match these filters.'}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Loading() {
  return (
    <Stack gap={1}>
      <Skeleton variant="rounded" height={40} />
      <Skeleton variant="rounded" height={160} />
    </Stack>
  );
}

export function MyRecommendationHistoryPage() {
  const history = useMyRecommendationHistory();
  const [show, setShow] = useState<HistoryFilter>('all');
  const [q, setQ] = useState('');
  const all = history.data?.rows;
  const rows = useMemo(() => filterRecommendationHistory(all ?? [], { show, q }), [all, show, q]);

  return (
    <Box sx={page}>
      <Typography variant="h2" mb={1}>
        Recommendation history
      </Typography>
      <Typography color="text.secondary" mb={2}>
        Each of your recommendations with the date it was approved (marked as one of your allotted recommendations), the date it was used (submitted to Admissions) and the date it expires (the end of
        its academic year).
      </Typography>
      <HistoryControls show={show} onShow={setShow} q={q} onQ={setQ} placeholder="Search family, child, reference" />
      {history.isPending ? (
        <Loading />
      ) : history.error ? (
        <Alert severity="error">Could not load your recommendation history: {describeError(history.error)}</Alert>
      ) : (
        <HistoryTable
          rows={rows}
          total={history.data.rows.length}
          family={(r) => (
            <Link component={RouterLink} to={`/my-referrals/${r.id}`} fontWeight={600}>
              {familyLabel(r)}
            </Link>
          )}
        />
      )}
    </Box>
  );
}

function TrusteeSummary({ trustees, limit, onPick }: { trustees: TrusteeRecommendationSummary[]; limit: number; onPick: (email: string) => void }) {
  const withActive = trustees.filter((t) => t.active > 0).length;
  return (
    <Paper component="section" aria-labelledby="trustee-activity" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h3" component="h3" id="trustee-activity">
        Trustee activity
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {withActive} of {trustees.length} trustees have an active recommendation (limit {limit} per academic year). Tracking only, not a ranking.
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="Recommendations by trustee">
          <TableHead>
            <TableRow>
              <TableCell>Trustee</TableCell>
              <TableCell align="right">Active</TableCell>
              <TableCell align="right">Expired</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Last used</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trustees.map((t) => (
              <TableRow key={t.trustee.id} data-testid="trustee-summary-row">
                <TableCell>
                  <Link component="button" onClick={() => onPick(t.trustee.email)} title={`Show ${trusteeName(t.trustee)}'s recommendations`}>
                    {trusteeName(t.trustee)}
                  </Link>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t.trustee.email}
                  </Typography>
                </TableCell>
                <TableCell align="right">{t.active}</TableCell>
                <TableCell align="right">{t.expired}</TableCell>
                <TableCell align="right">{t.total}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtInstant(t.lastUsedAt)}</TableCell>
              </TableRow>
            ))}
            {trustees.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No trustee has used a recommendation yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function RecommendationHistoryPage() {
  const history = useRecommendationHistory();
  const { can } = useAuth();
  const { set } = useUrlState();
  const [show, setShow] = useState<HistoryFilter>('all');
  const [q, setQ] = useState('');
  const all = history.data?.rows;
  const rows = useMemo(() => filterRecommendationHistory(all ?? [], { show, q }), [all, show, q]);
  const canOpen = can('households.read');

  const family = (r: StaffRecommendationHistoryRow) =>
    canOpen && r.householdId ? (
      <Link component="button" fontWeight={600} sx={{ textAlign: 'left' }} onClick={() => set({ h: r.householdId, r: r.id }, false)}>
        {familyLabel(r)}
      </Link>
    ) : (
      <Typography variant="body2" fontWeight={600}>
        {familyLabel(r)}
      </Typography>
    );

  return (
    <Box sx={page}>
      <Typography variant="h2" mb={1}>
        Recommendation history
      </Typography>
      <Typography color="text.secondary" mb={2}>
        Every submitted Trustee recommendation with the date it was approved (marked by the trustee), used (submitted to Admissions) and expires (the end of its academic year). Drafts are not shown.
      </Typography>
      {history.isPending ? (
        <Loading />
      ) : history.error ? (
        <Alert severity="error">Could not load the recommendation history: {describeError(history.error)}</Alert>
      ) : (
        <>
          <TrusteeSummary trustees={history.data.trustees} limit={history.data.limit} onPick={setQ} />
          <Typography variant="h3" component="h3" mb={1}>
            Recommendations
          </Typography>
          <HistoryControls show={show} onShow={setShow} q={q} onQ={setQ} placeholder="Search family, child, reference, trustee" />
          <HistoryTable
            rows={rows}
            total={history.data.rows.length}
            family={family}
            trusteeColumn={(r) => (
              <>
                {trusteeName(r.trustee)}
                <Typography variant="caption" color="text.secondary" display="block">
                  {r.trustee.email}
                </Typography>
              </>
            )}
          />
        </>
      )}
    </Box>
  );
}
